export interface SindicalTagStyle {
  borderColor: string;
  backgroundColor: string;
  /** Texto no tema claro */
  color: string;
  /** Texto no tema escuro */
  colorDark?: string;
}

export interface SindicalTagBadge {
  label: string;
  estilo: SindicalTagStyle;
}

export const ESTADOS_POR_UF: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins'
};

const ESTILO_ESTADO: SindicalTagStyle = {
  borderColor: '#2f6fed',
  backgroundColor: 'rgba(47, 111, 237, 0.12)',
  color: '#1f54c4',
  colorDark: '#d4defb'
};

const ESTILO_CIDADE: SindicalTagStyle = {
  borderColor: '#8e44ad',
  backgroundColor: 'rgba(142, 68, 173, 0.12)',
  color: '#6c3483',
  colorDark: '#e8d4f0'
};

const ESTILO_REGISTRO: SindicalTagStyle = {
  borderColor: '#5c6770',
  backgroundColor: 'rgba(92, 103, 112, 0.12)',
  color: '#3d464d',
  colorDark: '#e2e6ea'
};

const ESTILO_OUTROS: SindicalTagStyle = {
  borderColor: '#5b6abf',
  backgroundColor: 'rgba(91, 106, 191, 0.12)',
  color: '#3f4d99',
  colorDark: '#d8dcf5'
};

/** Aplica estilo da tag respeitando tema claro/escuro do body. */
export function estiloBadgeSindicalNgStyle(estilo: SindicalTagStyle): Record<string, string> {
  const isDark =
    typeof document !== 'undefined' &&
    document.body.classList.contains('dark-theme');

  return {
    'border-color': estilo.borderColor,
    'background-color': estilo.backgroundColor,
    color: isDark ? estilo.colorDark ?? estilo.color : estilo.color
  };
}

const PREFIXO_UF = 'UF:';
const PREFIXO_ESTADO = 'ESTADO:';
const PREFIXO_CIDADE = 'CIDADE:';
const PREFIXO_REGISTRO = 'REGISTRO:';

/** Presente em todo documento sindical; vira ruído se exibida linha a linha. */
const TAG_CONVENCAO_SINDICAL = 'CONVENCAO_SINDICAL';

/** Convenções territoriais podem listar dezenas de municípios. */
const LIMITE_CIDADES_VISIVEIS = 6;

function normalizarTags(tags: string[] | null | undefined): string[] {
  if (!Array.isArray(tags)) return [];
  return tags.map((tag) => String(tag ?? '').trim()).filter(Boolean);
}

function valorDaTag(tag: string, prefixo: string): string | null {
  if (!tag.toUpperCase().startsWith(prefixo)) return null;
  return tag.slice(prefixo.length).trim() || null;
}

function valoresDoPrefixo(tags: string[], prefixo: string): string[] {
  const encontrados = tags
    .map((tag) => valorDaTag(tag, prefixo))
    .filter((valor): valor is string => !!valor);

  return [...new Set(encontrados)];
}

function primeiroValor(tags: string[], prefixo: string): string | null {
  return valoresDoPrefixo(tags, prefixo)[0] ?? null;
}

function ehTagConhecida(tag: string): boolean {
  const chave = tag.toUpperCase();
  return (
    chave === TAG_CONVENCAO_SINDICAL ||
    chave.startsWith(PREFIXO_UF) ||
    chave.startsWith(PREFIXO_ESTADO) ||
    chave.startsWith(PREFIXO_CIDADE) ||
    chave.startsWith(PREFIXO_REGISTRO)
  );
}

export function descreverEstado(tags: string[] | null | undefined): string | null {
  const lista = normalizarTags(tags);
  const uf = primeiroValor(lista, PREFIXO_UF)?.toUpperCase() ?? null;
  const estado = primeiroValor(lista, PREFIXO_ESTADO) ?? (uf ? ESTADOS_POR_UF[uf] : null);

  if (uf && estado) return `${uf} - ${estado}`;
  return uf ?? estado;
}

export function extrairUf(tags: string[] | null | undefined): string | null {
  const uf = primeiroValor(normalizarTags(tags), PREFIXO_UF)?.toUpperCase() ?? null;
  return uf && ESTADOS_POR_UF[uf] ? uf : null;
}

export function tagUf(uf: string): string {
  return `${PREFIXO_UF}${uf.trim().toUpperCase()}`;
}

export function tagCidade(cidade: string): string {
  return `${PREFIXO_CIDADE}${cidade.trim()}`;
}

/** Valor especial do select: agrupa Santo André, São Bernardo e São Caetano. */
export const FILTRO_CIDADE_ABC = '__ABC__';

export const FILTRO_CIDADE_ABC_LABEL =
  'ABC (Santo André, São Bernardo e São Caetano)';

const CIDADES_ABC_NORMALIZADAS = [
  'SANTO ANDRE',
  'SAO BERNARDO',
  'SAO CAETANO'
] as const;

export function ehFiltroCidadeAbc(cidade: string | null | undefined): boolean {
  return (cidade ?? '').trim().toUpperCase() === FILTRO_CIDADE_ABC;
}

export function normalizarNomeCidade(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^A-Za-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

export function cidadePertenceAoAbc(cidade: string): boolean {
  const normalizada = normalizarNomeCidade(cidade);
  return CIDADES_ABC_NORMALIZADAS.some(
    (prefixo) => normalizada === prefixo || normalizada.startsWith(`${prefixo} `)
  );
}

export function documentoPertenceAoAbc(tags: string[] | null | undefined): boolean {
  return extrairCidades(tags).some(cidadePertenceAoAbc);
}

export function extrairCidades(tags: string[] | null | undefined): string[] {
  return valoresDoPrefixo(normalizarTags(tags), PREFIXO_CIDADE).sort((a, b) =>
    a.localeCompare(b, 'pt-BR')
  );
}

export function montarBadgesSindicais(tags: string[] | null | undefined): SindicalTagBadge[] {
  const lista = normalizarTags(tags);
  if (lista.length === 0) return [];

  const badges: SindicalTagBadge[] = [];

  const estado = descreverEstado(lista);
  if (estado) {
    badges.push({ label: estado, estilo: ESTILO_ESTADO });
  }

  const cidades = extrairCidades(lista);
  for (const cidade of cidades.slice(0, LIMITE_CIDADES_VISIVEIS)) {
    badges.push({ label: cidade, estilo: ESTILO_CIDADE });
  }

  const excedente = cidades.length - LIMITE_CIDADES_VISIVEIS;
  if (excedente > 0) {
    badges.push({ label: `+${excedente} cidade(s)`, estilo: ESTILO_CIDADE });
  }

  const registro = primeiroValor(lista, PREFIXO_REGISTRO);
  if (registro) {
    badges.push({ label: registro, estilo: ESTILO_REGISTRO });
  }

  for (const tag of lista.filter((item) => !ehTagConhecida(item))) {
    badges.push({ label: tag, estilo: ESTILO_OUTROS });
  }

  return badges;
}
